import i18n from '@/helper/i18n';
import { makeObservable, observable, action } from 'mobx';
import { groupByKey } from '@/helper/util';
import { Log } from '@/helper/log';
import { transform } from './parse';
import { AACheckoutConst } from '@/helper/constants';

const t = i18n.chain;
const METHOD_NAMES = {
  UPDATE_DATA: 'updateData',
} as const;

export default class FareSummaryStore {
  private CLASS_NAME = 'FareSummaryStore';
  @observable transactionCurrency = '';
  @observable priceChanged = false;

  @observable conversionRates = null;
  @observable transformedFareDetails: AACheckout.FareDetailItem[] = [];
  @observable plainAddonItems: AACheckout.PlainAddonItem[] = [];
  @observable addonTotalPrices: number[] = [];
  @observable journeyLegs: AACheckout.JourneyLeg[] = [];
  @observable showFareDetails = false;

  @observable baseFare = 0;
  @observable tax = 0;

  @observable legalAgreed = false;

  baseCurrency = '';
  originalTotalAmount = 0;

  constructor() {
    makeObservable(this);
  }

  get totalAmount() {
    return [this.originalTotalAmount, ...this.addonTotalPrices].reduce(
      (a, b) => a + b,
      0,
    );
  }

  get fareDetails(): AACheckout.FareDetailItem[] {
    const plainAddonItems = this.plainAddonItems;
    const journeyLegs = this.journeyLegs;

    // group addon items
    const fareBreakdown: AACheckout.FareBreakdownItem[] = [];

    fareBreakdown.push(
      ...this.generateAddonBreakdown(plainAddonItems, journeyLegs, false),
    );

    fareBreakdown.push(...this.generateInsuranceBreakdown(plainAddonItems));

    fareBreakdown.push(
      ...this.generateAddonBreakdown(plainAddonItems, journeyLegs, true),
    );

    return [
      ...this.transformedFareDetails,
      {
        fareType: 'addOns',
        currency: this.transactionCurrency,
        isDiscount: false,
        title: t.flights.aaCheckout.fareSummary.addOns,
        totalAmount: this.addonTotalPrices.reduce((a, b) => a + b, 0),
        fareBreakdown,
      },
    ];
  }

  generateAddonBreakdown(
    plainAddonItems: AACheckout.PlainAddonItem[],
    journeyLegs: AACheckout.JourneyLeg[],
    isWheelchair: boolean,
  ) {
    const fareBreakdown: AACheckout.FareBreakdownItem[] = [];
    const types = isWheelchair
      ? ['wheelchair']
      : ['bundle', 'fastPass', 'baggage', 'seat', 'meal', 'chg1'];

    if (isWheelchair) {
      fareBreakdown.push({
        currency: '',
        fare_title: t.flights.aaCheckout.ancillary.wheelchairSelector.title,
        type: 'header wheelchair',
      });
    }

    for (const journey of journeyLegs) {
      const allRoutes = [
        journey.origin.stationCode + journey.destination.stationCode,
      ].concat(
        journey.segments.map((s) => s.departureStation + s.arrivalStation),
      );

      const filteredItems: AACheckout.PlainAddonItem[] = plainAddonItems.filter(
        (a) =>
          allRoutes.includes(a.departureStation + a.arrivalStation) &&
          (types as string[]).includes(a.type),
      );

      if ((isWheelchair && filteredItems.length > 0) || !isWheelchair) {
        fareBreakdown.push({
          currency: '',
          fare_title: `${journey.origin.stationName} - ${journey.destination.stationName}`,
          type: 'header route',
        });
      }

      fareBreakdown.push(...this.groupBy(filteredItems));
    }

    return fareBreakdown;
  }

  generateInsuranceBreakdown(plainAddonItems: AACheckout.PlainAddonItem[]) {
    const fareBreakdown: AACheckout.FareBreakdownItem[] = [];
    const insuranceItems: AACheckout.PlainAddonItem[] = plainAddonItems.filter(
      (a) => a.type === 'insurance',
    );

    if (insuranceItems.length > 0) {
      fareBreakdown.push({
        currency: '',
        fare_title: t.flights.aaCheckout.ancillary.insurance.headerTitle,
        type: 'header',
      });

      fareBreakdown.push(...this.groupBy(insuranceItems));
    }

    return fareBreakdown;
  }

  generateTitle(firstItem: any, items: any[]): string {
    let title: string;

    switch (firstItem.type) {
      case 'seat':
        title = `${items.length} x ${firstItem.name}(${items
          .map((i) => i.properties.seatNumber)
          .join(',')})`;
        break;

      case 'fastPass':
      case 'bundle':
      case 'insurance':
      case 'chg1':
        title = firstItem.name;
        break;
      default:
        title = `${items.length} x ${firstItem.name}`;
        break;
    }

    return title;
  }

  groupBy(addonItems: AACheckout.PlainAddonItem[]) {
    const grouped = groupByKey(addonItems, 'groupBy');
    const fareBreakdown: any[] = [];
    for (const key in grouped) {
      const items = grouped[key];
      const firstItem = items[0];

      const price = items.map((i) => i.price).reduce((a, b) => a + b, 0);

      const title = this.generateTitle(firstItem, items);

      fareBreakdown.push({
        id: firstItem.id,
        currency: firstItem.currency,
        price,
        fare_title: title,
        type: firstItem.type,
      });

      if (firstItem.type === 'bundle') {
        this.handleBundleItems(firstItem, fareBreakdown);
      }

      if (firstItem.type === 'fastPass') {
        //special handling for fastPass
        fareBreakdown.push({
          currency: '',
          fare_title: `${items.length} x Xtra carry-on 7kg`,
          type: 'sub-breakdown',
        });
      }
    }

    return fareBreakdown;
  }

  private handleBundleItems(firstItem: any, fareBreakdown: any[]) {
    // if it is Premium Flatbed bundle, remove the price information
    if (
      firstItem.properties?.bundleName === AACheckoutConst.FLATBED_BUNDLE_NAME
    ) {
      const lastItem = fareBreakdown[fareBreakdown.length - 1];
      const { price, currency, ...rest } = lastItem;
      fareBreakdown[fareBreakdown.length - 1] = rest;
    }

    const bundleSubItems = this.generateBundleSubItems(firstItem);
    fareBreakdown.push(...bundleSubItems);
  }

  sortAndGroupSsrsByDisplayName(plainAddonItem: AACheckout.PlainAddonItem) {
    const order = ['baggage', 'seat', 'insurance', 'meals', 'chg1'];
    const grouped = {};

    (plainAddonItem.properties.includedSsrs as AACheckout.BundleIncludedSsr[])
      .slice()
      .sort((a, b) => {
        const orderA = order.indexOf(a.ancillaryType);
        const orderB = order.indexOf(b.ancillaryType);

        if (orderA !== -1 && orderB !== -1) {
          return orderA - orderB;
        }
        if (orderA !== -1) return -1;
        if (orderB !== -1) return 1;
        return 0;
      })
      .filter((ssr) => ssr.ancillaryType !== 'other')
      .forEach((ssr) => {
        ssr.journeyDetails.forEach((jd) => {
          jd.selectedSsrs.forEach((selectedSsr) => {
            const ssrDisplayName =
              ssr.ancillaryType === 'baggage'
                ? ssr.ssrName
                : selectedSsr.name || ssr.ssrName;

            if (!grouped[ssrDisplayName]) {
              grouped[ssrDisplayName] = [];
            }
            grouped[ssrDisplayName].push(ssr);
          });
        });

        // if selectedSsrs has not value, use ssrName
        if (ssr.journeyDetails.every((jd) => jd.selectedSsrs.length === 0)) {
          const ssrDisplayName = ssr.ssrName;

          if (!grouped[ssrDisplayName]) {
            grouped[ssrDisplayName] = [];
          }
          grouped[ssrDisplayName].push(ssr);
        }
      });

    return grouped;
  }

  generateBundleSubItems(plainAddonItem: AACheckout.PlainAddonItem) {
    const bundleSubItems: any[] = [];
    const groupedBySsrName = this.sortAndGroupSsrsByDisplayName(plainAddonItem);

    for (const ssrDisplayName in groupedBySsrName) {
      const ssrItems = groupedBySsrName[ssrDisplayName];
      const length = ssrItems.length;
      bundleSubItems.push({
        currency: '',
        fare_title: `${length} x ${ssrDisplayName}`,
        type: 'sub-breakdown',
      });
    }

    return bundleSubItems;
  }

  @action
  updateData = async (originalPageData) => {
    try {
      const {
        baseCurrency,
        currency,
        totolAmount,
        priceChanged,
        fareDetails,
        conversionRates,
      } = await transform(originalPageData);
      const baseFareItem = fareDetails.find((f) => f.fareType === 'baseFare');
      const taxesItem = fareDetails.find((f) => f.fareType === 'taxes');

      baseFareItem && (this.baseFare = baseFareItem.totalAmount);
      taxesItem && (this.tax = taxesItem.totalAmount);

      this.baseCurrency = baseCurrency;
      this.transactionCurrency = currency;
      this.originalTotalAmount = totolAmount;
      this.priceChanged = priceChanged;
      this.transformedFareDetails = fareDetails;
      this.conversionRates = conversionRates;
    } catch (e) {
      Log.errorData(e, this.CLASS_NAME, METHOD_NAMES.UPDATE_DATA);
    }
  };

  @action
  onLegalChange = async (checked: boolean) => {
    this.legalAgreed = checked;
  };

  @action
  toggleShowFareDetails = async () => {
    this.showFareDetails = !this.showFareDetails;
  };

  composeCheckoutData() {
    return {
      totalCartOrder: this.totalAmount,
      totalCardOrderCurrency: this.transactionCurrency,
      currencyDetails: {
        baseCurrency: this.baseCurrency,
        transactionCurrency: this.transactionCurrency,
      },
    };
  }
}
